import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import logger from '../utils/logger';

const REFERRER_STORAGE_KEY = 'pixley_referral_id';

/**
 * Hook to capture referral ID from URL and store in localStorage.
 * 
 * Usage: Call once in App.js
 * 
 * Behavior:
 * - Reads `ref` param from URL on mount
 * - Stores in localStorage under key 'pixley_referral_id'
 * - Removes `ref` from URL after storing (clean URL)
 * - Does NOT overwrite if already stored (first referrer wins, prevents overwrite during OAuth redirect)
 * - Does NOT affect routing or navigation
 * - Does NOT interfere with OAuth or onboarding
 * - Does NOT redirect to auth page
 * 
 * Why localStorage (not sessionStorage):
 * - Survives OAuth redirect (URL params are lost during redirect)
 * - Persists until explicitly cleared after onboarding
 * - More reliable for OAuth flows
 * 
 * Referral Flow (Aligned with Backend):
 * 1. User opens /storyverse?ref=UUID or /?ref=UUID
 * 2. Ref is captured and stored in localStorage (if not already present)
 * 3. Ref is removed from URL (clean URL, prevents confusion)
 * 4. User browses Storyverse as guest
 * 5. User decides to sign up (email or Google OAuth)
 * 6. Frontend passes referrer_user_id to backend during signup/OAuth
 * 7. Backend creates referral with status="pending"
 * 8. User completes onboarding
 * 9. Backend updates referral to status="completed" via analytics trigger
 * 10. Frontend clears localStorage after onboarding completion
 */
export const useReferralCapture = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(location.search);
      const ref = searchParams.get('ref');

      if (ref && ref.trim()) {
        // Only store if not already present (first referrer wins)
        const existingRef = localStorage.getItem(REFERRER_STORAGE_KEY);
        
        if (!existingRef) {
          const referrerId = ref.trim();
          localStorage.setItem(REFERRER_STORAGE_KEY, referrerId);
          logger.log('📌 Referral ID stored in localStorage:', referrerId);
        } else {
          logger.log('📌 Referral ID already stored, keeping existing:', existingRef);
        }

        // Remove ref from URL after storing (clean URL)
        searchParams.delete('ref');
        const newSearch = searchParams.toString();
        const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
        
        // Replace URL without reloading or triggering navigation
        navigate(newUrl, { replace: true });
        logger.log('🧹 Removed ref from URL, clean URL:', newUrl);
      }
    } catch (error) {
      logger.warn('⚠️ Failed to capture referral ID:', error);
    }
  }, [location.search, location.pathname, navigate]);

  return null; // This hook has no return value
};

/**
 * Get stored referral ID from localStorage
 */
export const getReferralId = () => {
  try {
    const ref = localStorage.getItem(REFERRER_STORAGE_KEY);
    return ref && ref.trim() ? ref.trim() : null;
  } catch {
    return null;
  }
};

/**
 * Clear stored referral ID from localStorage
 */
export const clearReferralId = () => {
  try {
    localStorage.removeItem(REFERRER_STORAGE_KEY);
    logger.log('🧹 Referral ID cleared from localStorage');
  } catch (error) {
    logger.warn('⚠️ Failed to clear referral ID:', error);
  }
};
